import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { KpiStrip, KpiTileLoading, type KpiItem } from '../components/KpiTile';
import { EmptyState } from '../components/EmptyState';
import { SkeletonRow } from '../components/SkeletonRow';
import {
  getOutreachDrafts,
  getEngagementHealth,
  generateOutreach,
  patchOutreachDraft,
  approveOutreachDraft,
  rejectOutreachDraft,
  skipOutreachDraft,
  suppressOutreachDraft,
  sendOutreachDraft,
  sendTestOutreachDraft,
  sendApprovedOutreach,
  getCoachLedger,
  getOutreachConfig,
} from '../lib/api';
import { buildDraftMailto } from '../lib/outreach-mailto';
import { adjacentDraftId, navigateAfterSaving, nextActionableDraftId } from '../lib/outreach-review';
import { CategoryPill } from '../components/CategoryPill';
import { fmt } from '../lib/format';
import type {
  LedgerEntry,
  OutreachDraft,
  OutreachDraftStatus,
  OutreachRuntimeConfig,
  OutreachWorkflow,
} from '../types';
import { ACTIVE_ENGAGEMENT_SCOPE, draftMatchesScope, type EngagementScope } from '../../../src/reports/shared/engagement-scope';
import { EngagementScopeFilters } from '../components/EngagementScopeFilters';

const WORKFLOW_OPTIONS: Array<{ value: OutreachWorkflow; label: string }> = [
  { value: 'stalled_start', label: 'Stalled start (#2)' },
  { value: 'stale_activity_30d', label: '30-day stale (#3)' },
  { value: 'coach_statement', label: 'Monthly statement (#1)' },
  { value: 'stale_activity', label: 'Ending soon / overdue' },
];

type BulkAction = 'approve' | 'reject' | 'skip' | 'suppress';
const BULK_LABEL: Record<BulkAction, string> = {
  approve: 'Approved',
  reject: 'Rejected',
  skip: 'Skipped',
  suppress: 'Suppressed',
};

const STATUS_META: Record<OutreachDraftStatus, { label: string; bg: string; color: string }> = {
  pending_review: { label: 'To review', bg: 'rgba(45,106,158,0.12)', color: 'var(--navy)' },
  edited: { label: 'Edited', bg: 'rgba(45,106,158,0.12)', color: 'var(--navy)' },
  approved: { label: 'Approved', bg: 'rgba(46,125,50,0.14)', color: '#2e7d32' },
  sending: { label: 'Sending…', bg: 'rgba(45,106,158,0.12)', color: 'var(--navy)' },
  sent: { label: 'Sent', bg: 'rgba(46,125,50,0.14)', color: '#2e7d32' },
  awaiting_reply: { label: 'Awaiting reply', bg: 'rgba(45,106,158,0.12)', color: 'var(--navy)' },
  replied: { label: 'Replied', bg: 'rgba(46,125,50,0.14)', color: '#2e7d32' },
  closed: { label: 'Closed', bg: 'rgba(120,120,120,0.16)', color: 'var(--mute)' },
  rejected: { label: 'Rejected', bg: 'rgba(120,120,120,0.16)', color: 'var(--mute)' },
  skipped: { label: 'Skipped', bg: 'rgba(120,120,120,0.16)', color: 'var(--mute)' },
  blocked: { label: 'Blocked — no email', bg: 'rgba(184,67,10,0.12)', color: 'var(--warn)' },
  failed: { label: 'Failed', bg: 'rgba(184,67,10,0.12)', color: 'var(--warn)' },
};

function OutreachStatusPill({ status }: { status: OutreachDraftStatus }): JSX.Element {
  const m = STATUS_META[status];
  return (
    <span className="pill" style={{ background: m.bg, color: m.color }}>
      <span className="pdot" />
      {m.label}
    </span>
  );
}

function summarizeSend(
  deliveryMode: OutreachRuntimeConfig['deliveryMode'],
  testRecipient: string | null,
  summary: Record<string, number>,
): string {
  const base = `Sent ${summary.sent ?? 0} · Failed ${summary.failed ?? 0} · Skipped ${summary.skipped ?? 0}`;
  if (deliveryMode === 'test') {
    return `${base}. Test copies went only to ${testRecipient ?? 'the configured test recipient'}; live drafts were unchanged.`;
  }
  if (deliveryMode === 'disabled') {
    return `${base} · Dry run ${summary.dry_run ?? 0}. Sending is OFF; no email was transmitted.`;
  }
  return base;
}

interface OutreachFilters {
  status: OutreachDraftStatus[];
  coach: string[];
}

const EMPTY_FILTERS: OutreachFilters = { status: [], coach: [] };

export function Outreach(): JSX.Element {
  const qc = useQueryClient();
  const [scope, setScope] = useState<EngagementScope>(ACTIVE_ENGAGEMENT_SCOPE);
  const { data: health } = useQuery({ queryKey: ['engagement-health'], queryFn: getEngagementHealth });
  const [workflow, setWorkflow] = useState<OutreachWorkflow>('stalled_start');
  const { data: runtimeConfig } = useQuery({
    queryKey: ['outreach-config'],
    queryFn: getOutreachConfig,
  });
  // The view + bulk-send are scoped to the selected workflow's latest run (the
  // monthly cron generates two runs at once, so "newest overall" isn't enough).
  const { data, isLoading } = useQuery({
    queryKey: ['outreach', workflow],
    queryFn: () => getOutreachDrafts(workflow),
  });

  const [filters, setFilters] = useState<OutreachFilters>(EMPTY_FILTERS);
  const [collapsed, setCollapsed] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const generate = useMutation({
    mutationFn: () => generateOutreach(workflow, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach'] }),
  });

  const sendApproved = useMutation({
    mutationFn: () => sendApprovedOutreach(workflow, undefined, filtered.filter((d) => d.status === 'approved').map((d) => d.id)),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['outreach'] });
      setBanner(summarizeSend(res.deliveryMode, res.testRecipient, res.summary));
    },
  });

  const confirmSendApproved = () => {
    const mode = runtimeConfig?.deliveryMode ?? 'disabled';
    const message = mode === 'test'
      ? `Send ${counts.approved} separate test ${counts.approved === 1 ? 'copy' : 'copies'} only to ${runtimeConfig?.testRecipient ?? 'the test mailbox'}? Intended recipients will be labeled and live draft state will not change.`
      : mode === 'live'
        ? `Send ${counts.approved} approved ${counts.approved === 1 ? 'draft' : 'drafts'} to the intended facilitators? This cannot be undone.`
        : `Preview ${counts.approved} approved ${counts.approved === 1 ? 'draft' : 'drafts'} as a dry run? No email will be sent.`;
    if (window.confirm(message)) sendApproved.mutate();
  };

  // Bulk action over the selected rows (inbox-style). The caller passes the exact
  // ids to act on (the currently-visible selection — see `selectedIds`), so the
  // action can never reach drafts hidden by a filter or absent after a refetch.
  // Each draft transition is conditional server-side, so drafts in the wrong
  // state are skipped, not errored.
  const bulk = useMutation({
    mutationFn: async ({ action, ids }: { action: BulkAction; ids: string[] }) => {
      const run = (id: string): Promise<void> =>
        action === 'approve'
          ? approveOutreachDraft(id)
          : action === 'reject'
            ? rejectOutreachDraft(id)
            : action === 'skip'
              ? skipOutreachDraft(id)
              : suppressOutreachDraft(id, 30);
      const results = await Promise.allSettled(ids.map(run));
      return { action, ok: results.filter((r) => r.status === 'fulfilled').length, total: ids.length };
    },
    onSuccess: ({ action, ok, total }) => {
      qc.invalidateQueries({ queryKey: ['outreach'] });
      setSelected(new Set());
      setBanner(`${BULK_LABEL[action]} ${ok} of ${total} selected${ok < total ? ` (${total - ok} weren't in a valid state)` : ''}.`);
    },
  });

  const drafts = useMemo(() => data?.drafts ?? [], [data]);
  const run = data?.run ?? null;

  const filtered = useMemo(() => {
    return drafts.filter((d) => {
      if (!draftMatchesScope(d.engagements, scope)) return false;
      if (filters.status.length && !filters.status.includes(d.status)) return false;
      if (filters.coach.length && !(d.coachName && filters.coach.includes(d.coachName))) return false;
      return true;
    });
  }, [drafts, filters, scope]);

  const counts = useMemo(() => {
    const c = { toReview: 0, approved: 0, blocked: 0, engagements: 0 };
    for (const d of filtered) {
      if (d.status === 'pending_review' || d.status === 'edited') c.toReview += 1;
      if (d.status === 'approved') c.approved += 1;
      if (d.status === 'blocked') c.blocked += 1;
      c.engagements += d.engagements.length;
    }
    return c;
  }, [filtered]);



  const openDraft = useMemo(() => drafts.find((d) => d.id === openId) ?? null, [drafts, openId]);
  const openPosition = openId ? filtered.findIndex((draft) => draft.id === openId) : -1;
  const previousDraftId = openId ? adjacentDraftId(filtered, openId, 'previous') : null;
  const nextDraftId = openId ? adjacentDraftId(filtered, openId, 'next') : null;

  // The visible selection: ids that are both selected AND pass the current
  // filter. Bulk actions and the "N selected" count both read this, so they
  // stay in lockstep — no acting on rows the reviewer can't see.
  const selectedIds = useMemo(
    () => filtered.filter((d) => selected.has(d.id)).map((d) => d.id),
    [filtered, selected],
  );
  const selectedCount = selectedIds.length;
  const allSelected = filtered.length > 0 && selectedCount === filtered.length;
  const toggleSelect = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((d) => d.id)));
  const clearSelection = () => setSelected(new Set());

  const kpis: KpiItem[] = run
    ? [
        { label: 'To review', value: counts.toReview, sub: 'pending or edited drafts' },
        { label: 'Approved', value: counts.approved, sub: 'ready to send (Phase 1)' },
        { label: 'Blocked', value: counts.blocked, sub: 'no coach email on file' },
        {
          label: 'Engagements',
          value: counts.engagements,
          sub: run.generatedAt ? `Generated ${fmt.dateTime(run.generatedAt)}` : '',
        },
      ]
    : [];

  const hasFilters = scope.statuses.length > 0 || scope.accounts.mode !== 'all' || filters.status.length + filters.coach.length > 0;

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd">
          <div>
            <div className="eb">Coach outreach</div>
            <h1 className="d-title">Review &amp; send draft nudges</h1>
            <div className="d-meta" style={{ marginTop: 8, fontSize: 13 }}>
              {run ? (
                <>
                  Latest batch <span className="d-tnum">{fmt.dateTime(run.generatedAt)}</span> ·{' '}
                  {run.draftsCount} drafts · {run.engagementsCount} engagements
                  {run.suppressedCount > 0 && <> · {run.suppressedCount} suppressed</>}
                </>
              ) : (
                'No batch yet — pick a workflow and generate to draft nudges for review.'
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              variant="secondary"
              icon="user"
              onClick={confirmSendApproved}
              disabled={counts.approved === 0 || sendApproved.isPending || !runtimeConfig}
              title={runtimeConfig?.deliveryMode === 'test' ? 'Send an isolated test copy of every approved draft' : 'Send every approved draft'}
            >
              {sendApproved.isPending
                ? 'Sending…'
                : runtimeConfig?.deliveryMode === 'test'
                  ? `Send approved tests (${counts.approved})`
                  : runtimeConfig?.deliveryMode === 'disabled'
                    ? `Preview approved (${counts.approved})`
                    : `Send approved (${counts.approved})`}
            </Button>
            <select
              className="input"
              value={workflow}
              onChange={(e) => { setWorkflow(e.target.value as OutreachWorkflow); setSelected(new Set()); }}
              disabled={generate.isPending}
              aria-label="Workflow to generate"
              style={{ border: '1px solid var(--border-1)' }}
            >
              {WORKFLOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Button
              variant="primary"
              icon="refresh"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
            >
              {generate.isPending ? 'Generating…' : 'Generate drafts'}
            </Button>
          </div>
        </div>

        <EngagementScopeFilters value={scope} onChange={setScope}
          accounts={[...(health?.engagements ?? []).map((e) => e.accountDisplayName || e.accountName || ''), ...drafts.flatMap((d) => d.engagements.map((e) => e.accountDisplayName || e.accountName || ''))].filter(Boolean)}
          statuses={(health?.engagements ?? []).map((e) => e.engagementStatus?.trim() || '(blank)')} />
        <div className="d-meta" style={{ padding: '8px 24px' }}>
          Status and reporting-account filters also apply to new batches. Existing emails are kept whole;
          mixed or legacy drafts may be hidden. Select all statuses/accounts to review them, or generate a new scoped batch.
          {run?.generationScope && <> This batch used: {run.generationScope.statuses.join(', ') || 'all statuses'}; accounts: {run.generationScope.accounts.mode} {run.generationScope.accounts.values.join(', ')}.</>}
        </div>
        {(generate.isError || sendApproved.isError) && <div className="banner err" role="alert">{String((generate.error ?? sendApproved.error) instanceof Error ? (generate.error ?? sendApproved.error)?.message : 'Action failed. Please retry.')}</div>}

        {runtimeConfig?.deliveryMode === 'test' && (
          <div
            className="d-meta"
            style={{
              margin: '4px 0 12px',
              padding: '10px 14px',
              background: 'rgba(184,67,10,0.08)',
              border: '1px solid rgba(184,67,10,0.25)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
            }}
          >
            <Icon name="alert" size={14} style={{ color: 'var(--warn)' }} />
            <strong style={{ color: 'var(--warn)' }}>TEST MODE</strong>
            <span>
              Individual and bulk test copies can be sent only to {runtimeConfig.testRecipient}; intended recipients
              are labeled, and facilitator delivery, CC/BCC, suppressions, and live draft transitions remain off.
            </span>
          </div>
        )}

        {banner && (
          <div
            className="d-meta"
            style={{ margin: '4px 0 12px', padding: '10px 14px', background: 'rgba(45,106,158,0.08)', border: '1px solid var(--border-1)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
          >
            <Icon name="alert" size={14} style={{ color: 'var(--navy)' }} />
            <span style={{ flex: 1 }}>{banner}</span>
            <span className="x" style={{ cursor: 'pointer' }} onClick={() => setBanner(null)}><Icon name="x" size={13} /></span>
          </div>
        )}

        {isLoading ? (
          <div className="kpi-strip">{[0, 1, 2, 3].map((i) => <KpiTileLoading key={i} />)}</div>
        ) : kpis.length ? (
          <KpiStrip items={kpis} />
        ) : null}

        <div className="page-body" style={{ flexDirection: 'row' }}>
          <OutreachFilterSidebar
            drafts={drafts}
            filters={filters}
            setFilters={setFilters}
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
          />

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div className="toolbar">
              <div className="left">
                {selectedCount > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13 }}>{selectedCount} selected</strong>
                    <Button size="sm" variant="primary" icon="check" onClick={() => bulk.mutate({ action: 'approve', ids: selectedIds })} disabled={bulk.isPending}>Approve</Button>
                    <Button size="sm" variant="ghost" onClick={() => bulk.mutate({ action: 'reject', ids: selectedIds })} disabled={bulk.isPending}>Reject</Button>
                    <Button size="sm" variant="ghost" onClick={() => bulk.mutate({ action: 'skip', ids: selectedIds })} disabled={bulk.isPending}>Skip</Button>
                    <Button size="sm" variant="ghost" onClick={() => bulk.mutate({ action: 'suppress', ids: selectedIds })} disabled={bulk.isPending}>Suppress 30d</Button>
                    <a className="btn-link" style={{ fontSize: 12 }} onClick={clearSelection}>Clear</a>
                  </div>
                ) : (
                  <>
                    <Icon name="search" size={14} style={{ color: 'var(--mute)' }} />
                    <span className="d-meta" style={{ fontSize: 13 }}>
                      Select rows for bulk actions, or click a row to review
                    </span>
                  </>
                )}
              </div>
              <div className="right">
                <span className="count">
                  <strong>{filtered.length}</strong> of {drafts.length} drafts
                  {hasFilters && <span> · filtered</span>}
                </span>
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
              {isLoading ? (
                <table className="tbl gen">
                  <thead><tr><th /><th>Status</th><th>Coach</th><th>Engagements</th><th>Subject</th><th /></tr></thead>
                  <tbody>{Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cells={6} />)}</tbody>
                </table>
              ) : filtered.length === 0 ? (
                <EmptyState
                  icon={drafts.length === 0 ? 'user' : 'filter'}
                  title={drafts.length === 0 ? 'No drafts yet — generate a batch' : 'Nothing matches these filters'}
                  body={drafts.length === 0
                    ? 'Pick a workflow and click “Generate drafts” to query Salesforce and build one draft per coach.'
                    : 'Try clearing filters.'}
                  cta={drafts.length === 0
                    ? <Button variant="primary" icon="refresh" onClick={() => generate.mutate()}>Generate now</Button>
                    : <Button variant="secondary" onClick={() => { setFilters(EMPTY_FILTERS); setScope({ statuses: [], accounts: { mode: 'all', values: [] } }); }}>Show all drafts</Button>}
                />
              ) : (
                <table className="tbl gen">
                  <thead>
                    <tr>
                      <th style={{ width: 1, paddingRight: 0 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                          onChange={toggleAll}
                          aria-label="Select all drafts"
                        />
                      </th>
                      <th>Status</th>
                      <th>Coach</th>
                      <th>Engagements</th>
                      <th>Subject</th>
                      <th style={{ width: 1 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d) => (
                      <tr
                        key={d.id}
                        className={selected.has(d.id) ? 'selected' : undefined}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setOpenId(d.id)}
                      >
                        <td onClick={(e) => e.stopPropagation()} style={{ paddingRight: 0 }}>
                          <input
                            type="checkbox"
                            checked={selected.has(d.id)}
                            onChange={() => toggleSelect(d.id)}
                            aria-label={`Select ${d.coachName ?? 'draft'}`}
                          />
                        </td>
                        <td><OutreachStatusPill status={d.status} /></td>
                        <td>
                          {d.coachName ?? <span className="em">—</span>}
                          <div className="d-meta" style={{ fontSize: 11 }}>
                            {d.recipientEmail ?? <span style={{ color: 'var(--warn)' }}>no email</span>}
                          </div>
                        </td>
                        <td className="d-tnum">{d.engagements.length}</td>
                        <td style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.subject}
                        </td>
                        <td style={{ paddingRight: 16 }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setOpenId(d.id); }}>
                              <Icon name="eye" size={13} />
                              Review
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {openDraft && (
        <OutreachReviewDrawer
          draft={openDraft}
          runtimeConfig={runtimeConfig}
          onClose={() => setOpenId(null)}
          onNavigate={setOpenId}
          onActionComplete={(draftId) => setOpenId(nextActionableDraftId(filtered, draftId))}
          previousDraftId={previousDraftId}
          nextDraftId={nextDraftId}
          position={openPosition >= 0 ? openPosition + 1 : 1}
          total={openPosition >= 0 ? filtered.length : 1}
        />
      )}
    </div>
  );
}

interface SidebarProps {
  drafts: OutreachDraft[];
  filters: OutreachFilters;
  setFilters(f: OutreachFilters): void;
  collapsed: boolean;
  onToggle(): void;
}

function OutreachFilterSidebar({ drafts, filters, setFilters, collapsed, onToggle }: SidebarProps): JSX.Element {
  const statuses = useMemo(
    () => [...new Set(drafts.map((d) => d.status))] as OutreachDraftStatus[],
    [drafts],
  );
  const coaches = useMemo(
    () => [...new Set(drafts.map((d) => d.coachName).filter((x): x is string => !!x))].sort(),
    [drafts],
  );
  const toggle = (key: keyof OutreachFilters, value: string) => {
    const current = filters[key] as string[];
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    setFilters({ ...filters, [key]: next });
  };

  const total = filters.status.length + filters.coach.length;

  return (
    <div className={'fsidebar' + (collapsed ? ' collapsed' : '')}>
      <div className="fsidebar-hd">
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="lbl">Filters</span>
            {total > 0 && (
              <span style={{ background: 'var(--accent)', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 99, fontWeight: 600 }}>
                {total}
              </span>
            )}
          </div>
        )}
        <div className="collapse-btn" onClick={onToggle} title={collapsed ? 'Expand filters' : 'Collapse filters'}>
          <Icon name={collapsed ? 'sidebarCollapsed' : 'sidebar'} size={15} />
        </div>
      </div>
      <div className="fsidebar-body">
        <div className="fs-group">
          <label>Draft status</label>
          <div className="chk-list" style={{ maxHeight: 'none' }}>
            {statuses.map((s) => (
              <label key={s}>
                <input type="checkbox" checked={filters.status.includes(s)} onChange={() => toggle('status', s)} />
                <span>{STATUS_META[s].label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Coach · <span style={{ color: 'var(--mute)' }}>{filters.coach.length}/{coaches.length}</span></label>
          <div className="chk-list">
            {coaches.map((c) => (
              <label key={c} title={c}>
                <input type="checkbox" checked={filters.coach.includes(c)} onChange={() => toggle('coach', c)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c}</span>
              </label>
            ))}
          </div>
        </div>

      </div>
      <div className="fsidebar-foot">
        <a className="btn-link" onClick={() => setFilters(EMPTY_FILTERS)}>Reset all filters</a>
      </div>
    </div>
  );
}

interface DrawerProps {
  draft: OutreachDraft;
  runtimeConfig?: OutreachRuntimeConfig;
  onClose(): void;
  onNavigate(draftId: string): void;
  onActionComplete(draftId: string): void;
  previousDraftId: string | null;
  nextDraftId: string | null;
  position: number;
  total: number;
}

function OutreachReviewDrawer({
  draft,
  runtimeConfig,
  onClose,
  onNavigate,
  onActionComplete,
  previousDraftId,
  nextDraftId,
  position,
  total,
}: DrawerProps): JSX.Element {
  const qc = useQueryClient();
  const [subject, setSubject] = useState(draft.subject);
  const [bodyMd, setBodyMd] = useState(draft.bodyMd);
  const [copied, setCopied] = useState(false);
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    setSubject(draft.subject);
    setBodyMd(draft.bodyMd);
    setSendMsg(null);
    setReviewError(null);
  }, [draft.id, draft.subject, draft.bodyMd]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const dirty = subject !== draft.subject || bodyMd !== draft.bodyMd;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['outreach'] });
  const saveDirty = async () => {
    if (dirty) await patchOutreachDraft(draft.id, { subject, bodyMd });
  };
  const showError = (err: Error) => setReviewError(err.message || 'The action failed. Please try again.');
  const finishAction = () => {
    void invalidate();
    onActionComplete(draft.id);
  };

  const save = useMutation({
    mutationFn: () => patchOutreachDraft(draft.id, { subject, bodyMd }),
    onMutate: () => setReviewError(null),
    onSuccess: () => { void invalidate(); },
    onError: showError,
  });
  const approve = useMutation({
    mutationFn: async () => {
      await saveDirty();
      await approveOutreachDraft(draft.id);
    },
    onMutate: () => setReviewError(null),
    onSuccess: finishAction,
    onError: showError,
  });
  const reject = useMutation({
    mutationFn: async () => {
      await saveDirty();
      await rejectOutreachDraft(draft.id);
    },
    onMutate: () => setReviewError(null),
    onSuccess: finishAction,
    onError: showError,
  });
  const skip = useMutation({
    mutationFn: async () => {
      await saveDirty();
      await skipOutreachDraft(draft.id);
    },
    onMutate: () => setReviewError(null),
    onSuccess: finishAction,
    onError: showError,
  });
  const suppress = useMutation({
    mutationFn: async () => {
      await saveDirty();
      await suppressOutreachDraft(draft.id, 30);
    },
    onMutate: () => setReviewError(null),
    onSuccess: finishAction,
    onError: showError,
  });
  const send = useMutation({
    mutationFn: () =>
      runtimeConfig?.deliveryMode === 'test'
        ? sendTestOutreachDraft(draft.id)
        : sendOutreachDraft(draft.id),
    onSuccess: ({ result }) => {
      invalidate();
      setSendMsg(
        result.outcome === 'sent'
          ? result.deliveryMode === 'test'
            ? `Test sent only to ${result.to}; intended facilitator was ${result.intendedTo}. The draft remains approved.`
            : `Sent to ${result.to ?? 'coach'}.`
          : result.outcome === 'dry_run'
            ? 'Dry run — sending is OFF, nothing was emailed (set OUTREACH_SEND_ENABLED=true to go live).'
            : result.outcome === 'failed'
              ? `Send failed: ${result.detail ?? 'unknown error'}`
              : `Skipped: ${result.detail ?? 'not sendable'}`,
      );
    },
    onError: showError,
  });

  const navigateTo = async (draftId: string | null) => {
    if (!draftId) return;
    setReviewError(null);
    setNavigating(true);
    try {
      await navigateAfterSaving({
        dirty,
        save: async () => {
          await saveDirty();
          await invalidate();
        },
        navigate: () => onNavigate(draftId),
      });
    } catch (err) {
      showError(err as Error);
    } finally {
      setNavigating(false);
    }
  };

  // Accumulated, human-verified context for this coach (epic ledger) — shown
  // in-flow so history informs the send decision right here.
  const coachLedger = useQuery({
    queryKey: ['coach-ledger', draft.recipientEmail],
    queryFn: () => getCoachLedger(draft.recipientEmail as string),
    enabled: !!draft.recipientEmail,
  });
  const ledger = coachLedger.data ?? [];

  const busy = navigating || save.isPending || approve.isPending || reject.isPending || skip.isPending || suppress.isPending || send.isPending;
  const mailto = buildDraftMailto({ ...draft, subject, bodyMd });

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-hd">
          <div>
            <div className="eb">Draft · {draft.coachName ?? 'Coach'}</div>
            <div className="nm">{draft.recipientEmail ?? <span style={{ color: 'var(--warn)' }}>No email on file</span>}</div>
            <div className="d-meta" style={{ marginTop: 6, display: 'flex', gap: 12, alignItems: 'center' }}>
              <OutreachStatusPill status={draft.status} />
              <span>{draft.engagements.length} engagement{draft.engagements.length === 1 ? '' : 's'}</span>
              {draft.reviewedByEmail && <span className="d-tnum">· {draft.reviewedByEmail}</span>}
            </div>
          </div>
          <div className="collapse-btn" onClick={onClose} title="Close (Esc)">
            <Icon name="x" size={16} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 20px', borderBottom: '1px solid var(--border-1)' }}>
          <span className="d-meta d-tnum" style={{ fontSize: 12 }}>Draft {position} of {total}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Button
              size="sm"
              variant="ghost"
              icon="chevronLeft"
              onClick={() => { void navigateTo(previousDraftId); }}
              disabled={!previousDraftId || busy}
              title="Previous visible draft"
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconRight="chevronRight"
              onClick={() => { void navigateTo(nextDraftId); }}
              disabled={!nextDraftId || busy}
              title="Next visible draft"
            >
              Next
            </Button>
          </div>
        </div>

        <div className="drawer-bd">
          {draft.status === 'blocked' && (
            <div className="drawer-section">
              <span className="pill" style={{ background: 'rgba(184,67,10,0.12)', color: 'var(--warn)' }}>
                <span className="pdot" />
                No coach email on file — can't send. Reject, skip, or add the email in Salesforce.
              </span>
            </div>
          )}
          {reviewError && (
            <div className="drawer-section">
              <span className="pill" style={{ background: 'rgba(184,67,10,0.12)', color: 'var(--warn)' }}>
                <span className="pdot" />
                Could not save or complete the action; this draft is still open. {reviewError}
              </span>
            </div>
          )}
          {sendMsg && (
            <div className="drawer-section">
              <span className="pill" style={{ background: 'rgba(45,106,158,0.12)', color: 'var(--navy)' }}>
                <span className="pdot" />
                {sendMsg}
              </span>
            </div>
          )}

          <div className="drawer-section">
            <div className="drawer-section-h">
              Engagements the coach will see ({draft.engagements.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {draft.engagements.map((e) => (
                <div key={e.opportunityId} style={{ borderLeft: '2px solid var(--border-2)', paddingLeft: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }}>
                    {[e.accountName, e.coacheeName ?? e.opportunityName].filter(Boolean).join(' — ') || '—'}
                    {e.projectNumber && <span className="d-meta" style={{ fontWeight: 400 }}> · {e.projectNumber}</span>}
                  </div>
                  <div className="d-meta" style={{ fontSize: 12, marginTop: 2, wordBreak: 'break-word' }}>
                    <span className="d-tnum">
                      {e.hoursCompleted ?? 0}{e.hoursContracted != null ? ` of ${e.hoursContracted}` : ''} hours
                    </span>
                    {e.sowStartDate && (
                      <> · started {fmt.date(e.sowStartDate)}{e.daysSinceStart != null ? ` (${e.daysSinceStart}d ago)` : ''}</>
                    )}
                    {e.lastSessionDate && <> · last session {fmt.date(e.lastSessionDate)}</>}
                    {e.sowEndDate && <> · ends {fmt.date(e.sowEndDate)}</>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">What this coach has told us</div>
            {!draft.recipientEmail ? (
              <div className="d-meta" style={{ fontSize: 13 }}>No email on file — no context lookup.</div>
            ) : coachLedger.isLoading ? (
              <div className="d-meta" style={{ fontSize: 13 }}>Loading context…</div>
            ) : ledger.length === 0 ? (
              <div className="d-meta" style={{ fontSize: 13 }}>
                No context captured yet — it builds up as the coach replies and you confirm.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ledger.map((e) => <CoachContextEntry key={e.id} entry={e} />)}
              </div>
            )}
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Subject</div>
            <input
              className="input"
              style={{ width: '100%' }}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="drawer-section">
            <div className="drawer-section-h">Body</div>
            <textarea
              className="input"
              style={{ width: '100%', minHeight: 300, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical' }}
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
            />
            {dirty && (
              <div style={{ marginTop: 8 }}>
                <Button size="sm" variant="secondary" icon="check" onClick={() => save.mutate()} disabled={busy}>
                  {save.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="drawer-foot">
          {mailto && runtimeConfig && runtimeConfig.deliveryMode !== 'test' && (
            <a
              className="btn btn-secondary btn-sm"
              href={mailto}
              onClick={() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              title="Open in your mail client"
            >
              <Icon name="user" size={13} />
              {copied ? 'Opened' : 'Copy / open email'}
            </a>
          )}
          <Button size="sm" variant="ghost" onClick={() => suppress.mutate()} disabled={busy} style={{ marginLeft: 'auto' }} title="Skip and don't nag about these engagements for 30 days">
            {suppress.isPending ? 'Suppressing…' : 'Suppress 30d'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => skip.mutate()} disabled={busy}>Skip</Button>
          <Button size="sm" variant="ghost" onClick={() => reject.mutate()} disabled={busy}>Reject</Button>
          {draft.status === 'approved' || draft.status === 'awaiting_reply' ? (
            <Button
              size="sm"
              variant="primary"
              icon="arrowRight"
              onClick={() => send.mutate()}
              disabled={busy || !runtimeConfig || !draft.recipientEmail || draft.status === 'awaiting_reply'}
              title={
                draft.status === 'awaiting_reply'
                  ? 'Already sent'
                  : runtimeConfig?.deliveryMode === 'test'
                    ? `Send only to ${runtimeConfig.testRecipient}`
                    : 'Send this draft'
              }
            >
              {send.isPending
                ? 'Sending…'
                : draft.status === 'awaiting_reply'
                  ? 'Sent'
                  : runtimeConfig?.deliveryMode === 'test'
                    ? `Send test to ${runtimeConfig.testRecipient}`
                    : 'Send'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              icon="check"
              onClick={() => approve.mutate()}
              disabled={busy || !draft.recipientEmail}
              title={!draft.recipientEmail ? "No email — can't approve" : undefined}
            >
              {approve.isPending ? 'Approving…' : 'Approve'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CoachContextEntry({ entry }: { entry: LedgerEntry }): JSX.Element {
  const who = [entry.coachee_name, entry.account_name].filter(Boolean).join(' · ') || 'Engagement';
  const verified = entry.review_status !== 'unverified';
  const stampDate = entry.reviewed_at ?? entry.created_at;
  return (
    <div style={{ borderLeft: '2px solid var(--border-2)', paddingLeft: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }}>{who}</span>
        {entry.categories.map((c) => <CategoryPill key={c} category={c} />)}
      </div>
      {entry.body && (
        <div className="d-meta" style={{ fontSize: 12, marginTop: 2, wordBreak: 'break-word' }}>{entry.body}</div>
      )}
      <div className="d-meta" style={{ fontSize: 11, marginTop: 2 }}>
        {verified ? (entry.review_status === 'edited' ? 'Relabeled' : 'Confirmed') : 'Unverified'}
        {stampDate ? ` · ${fmt.date(stampDate)}` : ''}
        {entry.promised_log_date ? ` · promised by ${fmt.date(entry.promised_log_date)}` : ''}
      </div>
    </div>
  );
}
